import { Lock, Plug, TrendingDown, TrendingUp } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { AVERTISSEMENT_DEFINITIF } from '@/features/bridge/bridge-etat';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';

const euros = (v) => (v == null ? '—' : `${v} €`);

/**
 * Ce que déclarer un actif fait au revenu, montré AVANT le geste.
 *
 * POURQUOI CE COMPOSANT EST SORTI DE LA FICHE CLIENT
 *
 * Il vivait dans `RaccordementAgregateurs`, et la page Bridge retirait un actif d'un
 * clic sur une corbeille, sans rien montrer. Deux écrans, deux règles pour le même
 * geste : on pouvait perdre 245 €/an depuis l'un sans que l'autre l'ait jamais dit.
 * Le deuxième usage impose l'extraction, pas une copie.
 *
 * `confirmation` vaut `{ titre, impact }`, où `impact` est la réponse de
 * `GET /bridge/administration/impact`. `null` ferme le dialogue.
 *
 * DÉCLARER EST DÉFINITIF DEPUIS LE 07/10/2026
 *
 * Le retrait a son propre dialogue (`RetraitDefinitif` : motif et nom ressaisi) : celui-ci
 * ne sert plus qu'à déclarer, et montre toujours l'avertissement avant le bouton. Quand le
 * serveur annonce un refus (`impact.refus` : actif déjà chez un autre agrégateur), le
 * bouton est désactivé : proposer « Déclarer » pour recevoir un 409 serait mentir.
 *
 * `children` s'affiche avant l'avertissement : un champ propre au geste (la référence
 * chez l'agrégateur, par exemple), jamais un second bouton.
 *
 * `prefixe` garde les `data-testid` historiques de la fiche client (`raccordement`)
 * sans les imposer à la page Bridge.
 */
export default function ConfirmationImpact({
  confirmation, enCours, onAnnuler, onConfirmer, prefixe = 'impact', children = null,
}) {
  const impact = confirmation?.impact;
  const refus = impact?.refus;
  const perte = Boolean(refus) || (impact && impact.ecart < 0);
  return (
    <Dialog open={Boolean(confirmation)} onOpenChange={(ouvert) => !ouvert && onAnnuler()}>
      <DialogContent className="max-w-lg" data-testid={`${prefixe}-impact`}>
        {impact && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                {perte
                  ? <TrendingDown className="h-4 w-4 text-red-600" aria-hidden />
                  : <TrendingUp className="h-4 w-4 text-emerald-600" aria-hidden />}
                {confirmation.titre}
              </DialogTitle>
              <DialogDescription
                className={`rounded-md px-3 py-2 text-[13px] leading-relaxed ${perte
                  ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}
                data-testid={`${prefixe}-verdict`}>
                {impact.verdict}
              </DialogDescription>
            </DialogHeader>

            <table className="w-full text-[12.5px] tabular-nums">
              <thead className="text-neutral-500">
                <tr>
                  <th className="text-left font-normal">Agrégateur</th>
                  <th className="text-right font-normal">Avant</th>
                  <th className="text-right font-normal">Après</th>
                </tr>
              </thead>
              <tbody>
                {impact.par_agregateur.map((l) => (
                  <tr key={l.agregateur} className="border-t border-neutral-100">
                    <td className="py-1.5">
                      {l.agregateur}
                      {l.profil_avant !== l.profil_apres && (
                        <span className="block text-[11.5px] text-neutral-500">
                          profil {l.profil_avant || '—'} → {l.profil_apres || '—'}
                        </span>
                      )}
                    </td>
                    <td className="text-right">{euros(l.avant)}</td>
                    <td className="text-right font-medium">{euros(l.apres)}</td>
                  </tr>
                ))}
                <tr className="border-t border-neutral-300 font-medium">
                  <td className="py-1.5">Total annuel</td>
                  <td className="text-right">{euros(impact.total_avant)}</td>
                  <td className="text-right">{euros(impact.total_apres)}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {confirmation && !refus && children}

        {confirmation && !refus && (
          <p className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2
                        text-[12.5px] leading-relaxed text-amber-900"
             data-testid={`${prefixe}-definitif`}>
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {AVERTISSEMENT_DEFINITIF}
          </p>
        )}

        {confirmation && (
          <DialogFooter>
            <Button size="sm" variant="outline" disabled={enCours} onClick={onAnnuler}>
              Annuler
            </Button>
            <Button size="sm" disabled={enCours || Boolean(refus)} onClick={onConfirmer}
                    variant={perte ? 'destructive' : 'default'}
                    data-testid={`${prefixe}-confirmer`}>
              <Plug className="mr-1.5 h-4 w-4" aria-hidden />
              {refus ? 'Déclaration impossible'
                : (perte ? 'Déclarer quand même' : 'Déclarer définitivement')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
